import React, { useState, useEffect } from 'react';
import { useApp } from '../hooks/useApp';
import { useLanguage } from '../contexts/LanguageContext';
import PageMeta from '../components/common/PageMeta';
import ProductGrid from '../components/product/ProductGrid';
import Sidebar from '../components/layout/Sidebar'; // Reusing existing Sidebar logic but wrapping it
import { getAllProducts } from '../services/supabase/products';
import { getAllCategories } from '../services/supabase/categories';
import { sortProductsForDisplay } from '../utils/productSort';
import Breadcrumb from '../components/common/Breadcrumb';
import { Filter, X, Search } from 'lucide-react';

const ShopPage = () => {
    const { searchQuery, setSearchQuery, selectedCategory, setSelectedCategory, setCurrentPage, settings } = useApp();
    const { language, t } = useLanguage();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [sortBy, setSortBy] = useState('newest'); // newest | price_asc | price_desc

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const [productsResult, categoriesResult] = await Promise.all([
                getAllProducts(true),
                getAllCategories()
            ]);

            if (productsResult.success) setProducts(productsResult.products);
            if (categoriesResult.success) setCategories(categoriesResult.categories);

            setLoading(false);
        };
        fetchData();
    }, []);

    // Filter products
    const filteredProducts = products.filter(product => {
        const searchTerms = searchQuery.toLowerCase().split(' ').filter(word => word.length > 0);

        const localizedName = product[`name_${language}`]?.toLowerCase() || product.name?.toLowerCase() || '';
        const code = product.size?.toLowerCase() || '';
        const colors = product.colors || [];
        const categoryName = product.categories?.name?.toLowerCase() || '';

        const matchesSearch = searchTerms.length === 0 || searchTerms.every(term => {
            const inName = localizedName.includes(term);
            const inCode = code.includes(term);
            const inColors = colors.some(c => c.toLowerCase().includes(term));
            const inCategory = categoryName.includes(term);

            return inName || inCode || inColors || inCategory;
        });

        const matchesCategory = !selectedCategory ||
            (product.categories?.name === selectedCategory.category);

        return matchesSearch && matchesCategory;
    });

    const sortedProducts = React.useMemo(() => {
        if (filteredProducts.length === 0) return [];

        const categoryKeyOf = (p) => p.categories?.name || p.category_id || 'uncategorized';

        // Bitta kategoriya: tartib ichkarida (sort_order → kod → nom → sana/narx)
        if (selectedCategory) {
            return sortProductsForDisplay(filteredProducts, sortBy, language);
        }

        // Barcha mahsulotlar: avval har bir kategoriya ichida tartib, keyin navbatma-navbat aralashtirish
        const byCategory = {};
        filteredProducts.forEach((p) => {
            const key = categoryKeyOf(p);
            if (!byCategory[key]) byCategory[key] = [];
            byCategory[key].push(p);
        });

        const categoryKeys = Object.keys(byCategory).sort((a, b) =>
            String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
        );

        const sortedByCategory = {};
        let total = 0;
        for (const key of categoryKeys) {
            sortedByCategory[key] = sortProductsForDisplay(byCategory[key], sortBy, language);
            total += sortedByCategory[key].length;
        }

        const interleaved = [];
        let idx = 0;
        while (interleaved.length < total) {
            for (const key of categoryKeys) {
                const row = sortedByCategory[key];
                if (row[idx] != null) interleaved.push(row[idx]);
            }
            idx++;
        }
        return interleaved;
    }, [filteredProducts, sortBy, selectedCategory, language]);

    const categoryDisplayName = selectedCategory
        ? (categories.find(c => c.name === selectedCategory.category)?.[`name_${language}`] || selectedCategory.category)
        : null;

    const breadcrumbItems = [
        { label: t('home'), onClick: () => setCurrentPage('home') },
        ...(selectedCategory
            ? [
                { label: t('shop'), onClick: () => { setSelectedCategory(null); setCurrentPage('shop'); } },
                { label: categoryDisplayName }
            ]
            : [{ label: t('shop') }])
    ];

    return (
        <>
            <PageMeta title={t('shop')} description={t('metaDescShop')} siteName={settings?.site_name} />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-6 sm:py-8">
            <Breadcrumb items={breadcrumbItems} />
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-8">
                <h1 className="text-3xl font-display font-bold text-gray-900 mb-4 md:mb-0">
                    {selectedCategory ? (
                        categories.find(c => c.name === selectedCategory.category)?.[`name_${language}`] || selectedCategory.category
                    ) : (t('shopAll') || 'Shop All Products')}
                </h1>

                <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
                    {/* Search Bar */}
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t('search') || "Search..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm text-sm"
                        />
                    </div>

                    <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto justify-between sm:justify-end pl-0 sm:pl-0">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="min-w-[120px] sm:min-w-0 flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        >
                            <option value="newest">{t('sortNewest') || 'Yangilar'}</option>
                            <option value="price_asc">{t('sortPriceAsc') || 'Narx: pastdan yuqoriga'}</option>
                            <option value="price_desc">{t('sortPriceDesc') || 'Narx: yuqoridan pastga'}</option>
                        </select>
                        <span className="text-gray-500 text-sm whitespace-nowrap shrink-0">{filteredProducts.length} {t('items')}</span>
                        <button
                            className="md:hidden flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-sm shadow-sm shrink-0"
                            onClick={() => setShowMobileFilters(true)}
                        >
                            <Filter className="w-4 h-4" /> {t('filter') || 'Filter'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex gap-8">
                {/* Sidebar (Desktop) */}
                <div className="hidden md:block w-64 flex-shrink-0">
                    <Sidebar />
                </div>

                {/* Mobile Filter - pastdan chiqadigan bottom sheet (yondan emas) */}
                {showMobileFilters && (
                    <div className="fixed inset-0 z-50 md:hidden" onClick={() => setShowMobileFilters(false)}>
                        <div className="absolute inset-0 bg-black/50 ui-modal-overlay" aria-hidden="true" />
                        <div
                            className="absolute bottom-0 left-0 right-0 max-h-[85vh] bg-white rounded-t-2xl shadow-2xl overflow-y-auto animate-slide-up"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-4 flex justify-between items-center rounded-t-2xl z-10">
                                <h2 className="text-lg font-bold text-gray-900">{t('filter') || 'Filter'}</h2>
                                <button
                                    onClick={() => setShowMobileFilters(false)}
                                    className="p-2 -m-2 rounded-full hover:bg-gray-100 transition-colors"
                                    aria-label="Yopish"
                                >
                                    <X className="w-5 h-5 text-gray-600" />
                                </button>
                            </div>
                            <div className="p-4 pb-8">
                                <Sidebar />
                            </div>
                        </div>
                    </div>
                )}

                {/* Product Grid */}
                <div className="flex-1 min-w-0 rounded-2xl bg-gray-50/80 p-2 sm:p-4 md:p-6">
                    {selectedCategory && (
                        <div className="mb-6 flex items-center">
                            <span className="text-gray-600 mr-2">{t('activeFilter') || 'Active Filter'}:</span>
                            <span className="inline-flex items-center px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                                {categories.find(c => c.name === selectedCategory.category)?.[`name_${language}`] || selectedCategory.category}
                                <button
                                    onClick={() => setSelectedCategory(null)}
                                    className="ml-2 hover:bg-primary/20 rounded-full p-0.5"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        </div>
                    )}

                    {loading ? (
                        <ProductGrid products={[]} loading={true} withSidebar />
                    ) : filteredProducts.length > 0 ? (
                        <ProductGrid products={sortedProducts} withSidebar />
                    ) : (
                        <div className="text-center py-20 bg-gray-50 rounded-xl">
                            <p className="text-gray-500 text-lg">No products found matching your criteria.</p>
                            <button
                                onClick={() => setSelectedCategory(null)}
                                className="mt-4 text-primary font-medium hover:underline"
                            >
                                Clear filters
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

export default ShopPage;
